// frontend/src/components/Navbar.js

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { FaSearch, FaTimes, FaChevronDown, FaBars } from 'react-icons/fa';
import './Navbar.css';
import logo from '../assets/images/logo.webp';
import categories from './categories'; // Use the centralized categories data
import SearchInputWithPredictions from './SearchInputWithPredictions'; // IMPORT THE NEW COMPONENT

const Navbar = ({
    onSearch,
    isMobileMenuOpen,
    toggleMobileMenu,
    closeMobileMenu,
    isMobileSearchActive,
    handleMobileSearchToggle,
    searchTerm,
    setSearchTerm,
    mobileSearchInputRef,
    allSpices // Passed from App.js to Navbar, and then to SearchInputWithPredictions
}) => {
    const [hidden, setHidden] = useState(false);
    const [isScrolled, setIsScrolled] = useState(false);
    const [isDesktopSearchExpanded, setIsDesktopSearchExpanded] = useState(false); // Renamed for clarity
    const [isCategoriesDropdownOpen, setIsCategoriesDropdownOpen] = useState(false);

    const lastScrollY = useRef(0);
    const desktopSearchInputRef = useRef(null); // Ref for desktop search input
    const categoriesDropdownRef = useRef(null);
    const location = useLocation();
    const navigate = useNavigate();
    const isHomePage = location.pathname === '/';

    const handleScroll = useCallback(() => {
        const currentScrollY = window.scrollY;

        if (isHomePage) {
            if (currentScrollY > lastScrollY.current && currentScrollY > 50) {
                setHidden(true);
            } else if (currentScrollY < lastScrollY.current || currentScrollY <= 50) {
                setHidden(false);
            }
        } else {
            setHidden(false);
        }
        lastScrollY.current = currentScrollY;

        if (currentScrollY > 80) {
            setIsScrolled(true);
        } else {
            setIsScrolled(false);
        }
    }, [isHomePage]);

    useEffect(() => {
        window.addEventListener('scroll', handleScroll);
        handleScroll();
        return () => {
            window.removeEventListener('scroll', handleScroll);
        };
    }, [handleScroll]);

    // Handle desktop search icon click to expand/collapse search input
    const handleDesktopSearchIconClick = () => {
        setIsDesktopSearchExpanded(prevState => {
            const newState = !prevState;
            if (newState) {
                setTimeout(() => {
                    desktopSearchInputRef.current?.focus(); // Focus input when expanded
                }, 100);
            } else {
                setSearchTerm(''); // Clear desktop search term
            }
            return newState;
        });
    };

    // --- Handlers for categories dropdown mouse events (Desktop only) ---
    const handleCategoriesMouseEnter = () => {
        setIsCategoriesDropdownOpen(true);
    };

    const handleCategoriesMouseLeave = () => {
        setIsCategoriesDropdownOpen(false);
    };

    // This hook ensures the mobile input is focused when it appears.
    useEffect(() => {
        if (isMobileSearchActive && mobileSearchInputRef.current) {
            mobileSearchInputRef.current.focus({ preventScroll: true });
        }
    }, [isMobileSearchActive, mobileSearchInputRef]);

    const navbarClass = `navbar ${hidden ? 'navbar-hidden' : ''} ${
        isHomePage ? (isScrolled ? 'navbar-scrolled' : '') : 'navbar-scrolled'
    } ${isMobileSearchActive ? 'search-active' : ''}`;
    const showSearchSection = !location.pathname.startsWith('/admin');

    return (
        <nav className={navbarClass}>
            <div className="navbar-logo">
                <Link to="/" onClick={closeMobileMenu}>
                    <img src={logo} alt="Green Pod Logo" className="logo-img" />
                </Link>
            </div>

            {/* Desktop Navigation Links & Search Bar (hidden on mobile via CSS) */}
            <div className="navbar-desktop-content">
                <ul className="navbar-menu">
                    <li
                        className="categories-dropdown-wrapper"
                        onMouseEnter={handleCategoriesMouseEnter}
                        onMouseLeave={handleCategoriesMouseLeave}
                        ref={categoriesDropdownRef}
                    >
                        <Link to="/#categories-section" className="categories-link">
                            CATEGORIES
                            <FaChevronDown className={`dropdown-arrow ${isCategoriesDropdownOpen ? 'open' : ''}`} />
                        </Link>
                        {isCategoriesDropdownOpen && (
                            <div className="category-dropdown">
                                {categories.map(category => (
                                    <Link
                                        to={`/products/${encodeURIComponent(category.backendValue)}`}
                                        key={category.id}
                                        className="dropdown-item"
                                        onClick={() => setIsCategoriesDropdownOpen(false)}
                                    >
                                        <img src={category.image} alt={category.name} />
                                        <span>{category.name}</span>
                                    </Link>
                                ))}
                            </div>
                        )}
                    </li>

                    <li><Link to="/products">PRODUCTS</Link></li>
                    <li><Link to="/#about-section">ABOUT US</Link></li>
                    <li><Link to="/#contact-section">CONTACT US</Link></li>
                </ul>

                {showSearchSection && (
                    <div className={`search-container ${isDesktopSearchExpanded ? 'expanded' : ''}`}>
                        {/* Desktop Search Input with Predictions */}
                        {isDesktopSearchExpanded ? (
                            <SearchInputWithPredictions
                                searchTerm={searchTerm}
                                setSearchTerm={setSearchTerm}
                                onSearch={(query) => {
                                    onSearch(query);
                                    setIsDesktopSearchExpanded(false); // Collapse after search
                                }}
                                allSpices={allSpices}
                                inputRef={desktopSearchInputRef}
                                placeholder="Search spices..."
                                isMobile={false}
                            />
                        ) : (
                            <button className="search-toggle" onClick={handleDesktopSearchIconClick}>
                                <FaSearch />
                            </button>
                        )}
                        {isDesktopSearchExpanded && (
                            <button className="search-toggle" onClick={handleDesktopSearchIconClick}>
                                <FaTimes />
                            </button>
                        )}
                    </div>
                )}
            </div>

            {/* Mobile Icons Group (visible only on mobile via CSS) */}
            {!isMobileSearchActive && (
                <div className="navbar-mobile-icons">
                    {showSearchSection && (
                        <div className="mobile-search-icon-wrapper">
                            <FaSearch className="mobile-search-toggle" onClick={() => handleMobileSearchToggle(true)} />
                        </div>
                    )}
                    <div className="mobile-menu-toggle" onClick={toggleMobileMenu}>
                        <FaBars />
                    </div>
                </div>
            )}

            {/* Mobile Top-Level Search Box (appears over navbar when active) */}
            {isMobileSearchActive && showSearchSection && (
                <div className="mobile-top-search-box">
                    {/* Mobile Search Input with Predictions */}
                    <SearchInputWithPredictions
                        searchTerm={searchTerm}
                        setSearchTerm={setSearchTerm}
                        onSearch={(query) => {
                            onSearch(query);
                            handleMobileSearchToggle(false); // Close mobile search after search
                        }}
                        allSpices={allSpices}
                        inputRef={mobileSearchInputRef} // This ref is passed from App.js
                        placeholder="Search spices..."
                        isMobile={true}
                        onClose={() => handleMobileSearchToggle(false)} // Pass close handler for the 'X' button
                    />
                </div>
            )}
        </nav>
    );
};

export default Navbar;